import type { CSSProperties, ReactNode } from 'react';

import Skeleton from 'components/Skeleton';

import DashboardCard from '../DashboardCard';
import Grid from '../Grid';

import styles from './dashboard-skeleton.module.scss';

type SkeletonStyle = CSSProperties & {
  '--card-delay': string;
};

const cardStyle = (delay: string): SkeletonStyle => ({ '--card-delay': delay });

function CardFrame({ delay, children }: { delay: string; children: ReactNode }) {
  return (
    <div className={styles.card} style={cardStyle(delay)}>
      {children}
    </div>
  );
}

function CardTitle({ width = 110, subtitleWidth }: { width?: number; subtitleWidth?: number }) {
  return (
    <span className={styles.title}>
      <Skeleton width={width} height={11} />
      {subtitleWidth && <Skeleton width={subtitleWidth} height={9} />}
    </span>
  );
}

function MetricPlaceholder({ delay, titleWidth = 110 }: { delay: string; titleWidth?: number }) {
  return (
    <CardFrame delay={delay}>
      <DashboardCard title={<CardTitle width={titleWidth} subtitleWidth={142} />}>
        <div className={styles.metric}>
          <Skeleton width={92} height={56} />
          <Skeleton width={124} height={22} radius={999} />
        </div>
      </DashboardCard>
    </CardFrame>
  );
}

export default function DashboardSkeleton() {
  const distributionHeights = [42, 72, 94, 64, 108, 54, 82];
  const podiumHeights = [76, 108, 62];

  return (
    <div className={styles.wrapper} role="status" aria-busy="true">
      <span className="sr-only">Loading dashboard</span>
      <div className={styles.content} aria-hidden="true">
        <Grid>
          <Grid.Item span={1}>
            <MetricPlaceholder delay="0s" />
          </Grid.Item>

          <Grid.Item span={1}>
            <CardFrame delay="0.05s">
              <DashboardCard title={<CardTitle width={136} subtitleWidth={98} />}>
                <div className={styles.distribution}>
                  {distributionHeights.map((height, index) => (
                    <Skeleton key={index} className={styles.distributionBar} height={height} radius="4px 4px 0 0" />
                  ))}
                </div>
              </DashboardCard>
            </CardFrame>
          </Grid.Item>

          <Grid.Item span={1}>
            <MetricPlaceholder delay="0.1s" titleWidth={156} />
          </Grid.Item>

          <Grid.Item span={1}>
            <MetricPlaceholder delay="0.15s" titleWidth={156} />
          </Grid.Item>

          <Grid.Item span={2}>
            <CardFrame delay="0.2s">
              <DashboardCard
                title={<CardTitle width={148} subtitleWidth={126} />}
                headerAction={<Skeleton width={116} height={24} radius={999} />}
              >
                <Skeleton className={styles.trend} width="100%" height="100%" radius={12} />
              </DashboardCard>
            </CardFrame>
          </Grid.Item>

          <Grid.Item span={2}>
            <CardFrame delay="0.25s">
              <DashboardCard title={<CardTitle width={142} subtitleWidth={98} />}>
                <div className={styles.leaderboard}>
                  <div className={styles.podium}>
                    {podiumHeights.map((height, index) => (
                      <div className={styles.podiumEntry} key={index}>
                        <Skeleton width={40} height={40} circle />
                        <Skeleton className={styles.podiumBar} width="100%" height={height} radius="8px 8px 0 0" />
                      </div>
                    ))}
                  </div>
                  <div className={styles.runners}>
                    {[0, 1].map((runner) => (
                      <div className={styles.runner} key={runner}>
                        <Skeleton width={28} height={28} circle />
                        <Skeleton width={92} height={10} />
                        <Skeleton className={styles.runnerScore} width={36} height={10} />
                      </div>
                    ))}
                  </div>
                </div>
              </DashboardCard>
            </CardFrame>
          </Grid.Item>
        </Grid>
      </div>
    </div>
  );
}
